import React, {FC, RefObject, useRef} from 'react';
import {DefaultTheme} from 'styled-components';

import {useSelectedConversationsDetails} from '../../../../libs/inboxes/business/src/cells/conversations';
import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {FirstConversationArchiveInfoBubble} from '../../infoBubbles/firstConversationArchiveInfoBubble';
import {ConversationsArchiveButtonProps} from '../viewer/conversation/actions/archive/conversationsArchiveCoordinator';
import {ConversationsArchiveCoordinatorContainer} from '../viewer/conversation/actions/archive/conversationsArchiveCoordinatorContainer';
import {makeInboxesTopbarArchiveTooltipProps} from './inboxesTopbarCommon';
import {InboxesTopbarMainButton} from './inboxesTopbarMainButton';

/*
 * Props.
 */

interface InboxesTopbarMainArchiveProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
}

/*
 * Component.
 */

export const InboxesTopbarMainArchive: FC<InboxesTopbarMainArchiveProps> = (props) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const {shouldDisableBulkActions} = useSelectedConversationsDetails(props);

  return (
    <ConversationsArchiveCoordinatorContainer
      panelId={props.panelId}
      cellsContext={props.cellsContext}
      inboxesParameters={props.inboxesParameters}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      renderButton={(archiveProps) => renderButton(buttonRef, archiveProps, shouldDisableBulkActions, props)}
    />
  );
};

/*
 * Helpers.
 */

function renderButton(
  buttonRef: RefObject<HTMLDivElement>,
  archiveProps: ConversationsArchiveButtonProps,
  shouldDisableBulkActions: boolean,
  props: InboxesTopbarMainArchiveProps,
) {
  const isDisabled = !archiveProps.permissionResult.isAllowed || shouldDisableBulkActions;
  const {permissionResult, onClick} = archiveProps;
  const tooltipProps = makeInboxesTopbarArchiveTooltipProps(archiveProps, props);

  const computeIconColor = (theme: DefaultTheme) => {
    if (isDisabled) {
      return theme.greys.shade70;
    }
    if (archiveProps.areAllArchived) {
      return theme.palette.green.shade40;
    }
    return undefined;
  };

  const icon = archiveProps.areAllArchived ? (
    <UserThemeConsumer>
      {(theme) => <Icon color={computeIconColor(theme)} name="archiveFilled" />}
    </UserThemeConsumer>
  ) : (
    <Icon name="archive" />
  );

  return (
    <>
      <InboxesTopbarMainButton
        isDisabled={isDisabled}
        shouldDisableBulkActions={shouldDisableBulkActions}
        ref={buttonRef}
        permissionResult={permissionResult}
        onClick={onClick}
        keyboardAction={KeyboardActionsEnum.ARCHIVE}
        tooltipFirstLine={tooltipProps.firstLine || tooltipProps.mainButton}
        tooltipSecondLine={tooltipProps.secondLine}
        icon={icon}
        conversations={props.conversations}
        cellsContext={props.cellsContext}
      >
        {tooltipProps.mainButton}
      </InboxesTopbarMainButton>
      <FirstConversationArchiveInfoBubble anchor={buttonRef} />
    </>
  );
}
